import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { baseURL, socketURL } from '../config/baseUrl';
import { generateDeviceId } from '../lib/generateDeviceId';
import Preview from './Preview';

interface TemplateDetailsResponse {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  htmlContent?: string;
  fontFamily?: string;
  themeColor?: string;
  backgroundColor?: string;
  logoUrl?: string;
  categories: CategoryDetailsResponse[];
}

interface CategoryDetailsResponse {
  id: string;
  categoryName: string;
  description?: string;
  icon?: string;
  imageUrl?: string;
  sortOrder: number;
  isActive: boolean;
  products: ProductDetailsResponse[];
}

interface ProductDetailsResponse {
  id: string;
  itemName: string;
  businessType: string;
  description?: string;
  ingredients?: string;
  preparationSteps?: string;
  chefPreferred?: string;
  goesWellWithIds?: string;
  crossSellImages?: string;
  regularPrice?: number;
  discountPrice?: number;
  happyHour?: string;
  availability?: string;
  allergens?: string;
  calories?: number;
  protein?: number;
  fat?: number;
  carbohydrates?: number;
  sugar?: number;
  fiber?: number;
  sodium?: number;
  imageUrls?: string;
  videoUrls?: string;
  highlight?: string;
  displayOrder?: number;
  campaign?: string;
  startDate?: string;
  endDate?: string;
  sortOrder: number;
  isActive: boolean;
}

interface DeviceStatusResponse {
  success: boolean;
  data?: {
    id: string;
    name: string;
    digitalDeviceId: string;
    status: string;
    tenantId: string;
    tenantName?: string;
    digitalMenuTenantTemplateId?: string;
    createdAt: string;
    updatedAt: string;
    templateDetails?: TemplateDetailsResponse | null;
  };
  error?: {
    message: string;
  };
}

interface TenantDetailsResponse {
  success: boolean;
  data?: {
    id: string;
    name: string;
    email: string;
    phone: string;
    address: string;
    isActive: boolean;
    plan: string;
    subscriptionStatus: string;
  };
  error?: {
    message: string;
  };
}

interface AddDeviceResponse {
  success: boolean;
  data?: {
    id: string;
    name: string;
    digitalDeviceId: string;
    status: string;
    tenantId: string;
    digitalMenuTenantTemplateId?: string;
    createdAt: string;
    updatedAt: string;
  };
  error?: {
    message: string;
  };
}

type ScreenState =
  | 'loading'
  | 'pin'
  | 'device-name'
  | 'pending'
  | 'waiting-content'
  | 'error';

export default function HomePage() {
  const [deviceId, setDeviceId] = useState('');
  const [currentScreen, setCurrentScreen] = useState<ScreenState>('loading');
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatusResponse | null>(
    null
  );
  const [contentRefreshKey, setContentRefreshKey] = useState(0);
  const [pin, setPin] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [tenantDetails, setTenantDetails] =
    useState<TenantDetailsResponse | null>(null);
  const [pinLoading, setPinLoading] = useState(false);
  const [deviceRegistrationLoading, setDeviceRegistrationLoading] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const initializeDevice = async () => {
      try {
        const generatedId = await generateDeviceId();
        setDeviceId(generatedId);
        checkDeviceStatus(generatedId);
      } catch (error) {
        console.error('Failed to initialize device:', error);
        setErrorMessage('Failed to initialize device. Please refresh the page.');
      }
    };

    initializeDevice();
  }, []);

  useEffect(() => {
    if (!deviceId) return;

    const socket = io(socketURL, { path: '/socket.io/', transports: ['websocket', 'polling'] });
    socket.emit('join-digital-menu-device-room', deviceId);
    socket.on('refresh-device-status', () => checkDeviceStatus(deviceId));

    return () => {
      socket.off('refresh-device-status');
      socket.disconnect();
    };
  }, [deviceId]);

  const checkDeviceStatus = async (deviceId: string) => {
    try {
      const response = await fetch(
        `${baseURL}/api/v1/digital-menu-devices/status/${deviceId}`
      );
      const data: DeviceStatusResponse = await response.json();

      if (data.success) {
        if (data.data?.templateDetails) {
          setDeviceStatus(data);
          setContentRefreshKey(k => k + 1);
          setCurrentScreen('waiting-content');
        } else {
          setCurrentScreen('pending');
        }
      } else if (data.error?.message === 'Device is not there') {
        setCurrentScreen('pin');
      } else if (data.error?.message === 'Device is not registered') {
        setCurrentScreen('pending');
      } else {
        setErrorMessage(data.error?.message || 'Something went wrong');
        setCurrentScreen('error');
      }
    } catch (error) {
      console.error('Error checking device status:', error);
      setErrorMessage('Failed to connect to server');
      setCurrentScreen('error');
    }
  };

  const verifyPin = async () => {
    if (!pin || pin.length !== 4) {
      return;
    }

    setPinLoading(true);
    try {
      const response = await fetch(
        `${baseURL}/api/v1/digital-menu-pins/check-tenant`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ pin }),
        }
      );
      const data: TenantDetailsResponse = await response.json();
      setTenantDetails(data);

      if (data.success) {
        setCurrentScreen('device-name');
      } else {
        setErrorMessage(data.error?.message || 'Invalid PIN');
        setCurrentScreen('error');
      }
    } catch (error) {
      console.error('Error verifying PIN:', error);
      setErrorMessage('Failed to verify PIN');
      setCurrentScreen('error');
    } finally {
      setPinLoading(false);
    }
  };

  const registerDevice = async () => {
    if (!deviceName.trim() || !tenantDetails?.data) {
      return;
    }

    setDeviceRegistrationLoading(true);
    try {
      const response = await fetch(
        `${baseURL}/api/v1/digital-menu-devices/add-device`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            deviceId: deviceId,
            deviceName: deviceName.trim(),
            tenantId: tenantDetails.data.id,
          }),
        }
      );
      const data: AddDeviceResponse = await response.json();

      if (data.success) {
        localStorage.setItem('deviceId', deviceId);
        setCurrentScreen('pending');
      } else {
        setErrorMessage(data.error?.message || 'Failed to register device');
        setCurrentScreen('error');
      }
    } catch (error) {
      console.error('Error registering device:', error);
      setErrorMessage('Failed to register device');
      setCurrentScreen('error');
    } finally {
      setDeviceRegistrationLoading(false);
    }
  };

  const resetToPin = () => {
    setPin('');
    setDeviceName('');
    setTenantDetails(null);
    setErrorMessage('');
    setCurrentScreen('pin');
  };

  const renderLoadingScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-700">
          Setting up your device...
        </h2>
      </div>
    </div>
  );

  const renderPinScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Enter PIN Code
          </h1>
          <p className="text-gray-600">Please enter the 4-digit PIN.</p>
        </div>

        <div className="space-y-6">
          <div>
            <input
              type="text"
              value={pin}
              onChange={e =>
                setPin(e.target.value.replace(/\D/g, '').slice(0, 4))
              }
              placeholder="0000"
              className="w-full text-center text-4xl font-mono tracking-widest border-2 border-gray-300 rounded-xl py-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              maxLength={4}
            />
          </div>

          <button
            onClick={verifyPin}
            disabled={pin.length !== 4 || pinLoading}
            className="w-full bg-blue-600 text-white py-4 px-6 rounded-xl text-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {pinLoading ? 'Verifying...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderDeviceNameScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Device Setup
          </h1>
          <p className="text-gray-600">
            Give your device a name for easy identification
          </p>
        </div>

        <div className="space-y-6">
          <div>
            <input
              type="text"
              value={deviceName}
              onChange={e => setDeviceName(e.target.value)}
              placeholder="e.g., Bar Tablet, Dine In Tablet"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
            />
          </div>

          <button
            onClick={registerDevice}
            disabled={!deviceName.trim() || deviceRegistrationLoading}
            className="w-full bg-green-600 text-white py-4 px-6 rounded-xl text-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {deviceRegistrationLoading ? 'Setting up...' : 'Complete Setup'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderPendingScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-amber-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg
            className="w-8 h-8 text-yellow-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          Device Registered
        </h1>
        <p className="text-gray-600 mb-6">
          Your device is now registered and waiting for content to be assigned.
        </p>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 font-medium">
            Status: Pending Approval
          </p>
        </div>
      </div>
    </div>
  );

  const renderWaitingContentScreen = () => {
    if (deviceStatus?.data?.templateDetails) {
      return (
        <Preview
          key={contentRefreshKey}
          templateDetails={deviceStatus.data.templateDetails as any}
          tenantId={deviceStatus.data.tenantId}
          tenantName={deviceStatus.data.tenantName}
        />
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Waiting for Content
          </h1>
          <p className="text-gray-600 mb-6">
            Your device is ready and waiting for digital menu content to be
            assigned.
          </p>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-purple-800 font-medium">
              Status: Ready for Content
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderErrorScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          Something Went Wrong
        </h1>
        <p className="text-gray-600 mb-6">{errorMessage}</p>
        <button
          onClick={resetToPin}
          className="w-full bg-red-600 text-white py-4 px-6 rounded-xl text-lg font-semibold hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  switch (currentScreen) {
    case 'loading':
      return renderLoadingScreen();
    case 'pin':
      return renderPinScreen();
    case 'device-name':
      return renderDeviceNameScreen();
    case 'pending':
      return renderPendingScreen();
    case 'waiting-content':
      return renderWaitingContentScreen();
    case 'error':
      return renderErrorScreen();
    default:
      return renderLoadingScreen();
  }
}
